import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocalization } from '../../contexts/LocalizationContext';
import advertisementService from '../../services/advertisementService';
import imageService from '../../services/imageService';
import { getProductImageUrl } from '../../utils/formatters';
import Loading from '../../components/Common/Loading';
import Button from '../../components/Common/Button';
import Input from '../../components/Common/Input';
import Modal from '../../components/Common/Modal';
import TargetPicker from './TargetPicker';
import AdvertisementPreview from './preview/AdvertisementPreview';
import {
  AdvertisementType, AdvertisementMediaType, AdvertisementPlatform, AdvertisementPage,
  AdvertisementActionType, AdvertisementStatus, PopupTriggerType, PopupFrequency,
  TYPE_LABELS, PLATFORM_LABELS, ACTION_TYPE_LABELS, TRIGGER_TYPE_LABELS, FREQUENCY_LABELS,
  labelFor, toIraqLocalInputValue, fromIraqLocalInputValue,
} from './adConstants';

const newPlacementKey = () => `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const AdvertisementForm = ({ mode, id }) => {
  const { isRtl } = useLocalization();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const prevTypeRef = useRef(AdvertisementType.Carousel);

  const [loadingInitial, setLoadingInitial] = useState(mode === 'edit');
  const [loadError, setLoadError] = useState(null);
  const [existingStatus, setExistingStatus] = useState(null);

  const [internalName, setInternalName] = useState('');
  const [type, setType] = useState(AdvertisementType.Carousel);
  const [titleEn, setTitleEn] = useState('');
  const [titleAr, setTitleAr] = useState('');
  const [descriptionEn, setDescriptionEn] = useState('');
  const [descriptionAr, setDescriptionAr] = useState('');

  const [mediaType, setMediaType] = useState(AdvertisementMediaType.Image);
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState('');

  const [showCta, setShowCta] = useState(true);
  const [ctaTextEn, setCtaTextEn] = useState('');
  const [ctaTextAr, setCtaTextAr] = useState('');
  const [isClickable, setIsClickable] = useState(true);

  const [actionType, setActionType] = useState(AdvertisementActionType.None);
  const [actionTargetId, setActionTargetId] = useState(null);
  const [actionUrl, setActionUrl] = useState('');

  const [startAtLocal, setStartAtLocal] = useState('');
  const [expireAtLocal, setExpireAtLocal] = useState('');

  const [popupTriggerType, setPopupTriggerType] = useState(PopupTriggerType.Immediate);
  const [popupTriggerValue, setPopupTriggerValue] = useState('');
  const [popupFrequency, setPopupFrequency] = useState(PopupFrequency.OncePerSession);
  const [newsBarIsScrolling, setNewsBarIsScrolling] = useState(false);

  const [placements, setPlacements] = useState([]);
  const [registryCache, setRegistryCache] = useState({});
  const [registryLoading, setRegistryLoading] = useState(false);
  const [pendingTypeChange, setPendingTypeChange] = useState(null);

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // ---- Load existing ad (edit mode) ----
  useEffect(() => {
    if (mode !== 'edit' || !id) return undefined;
    let cancelled = false;
    setLoadingInitial(true);
    setLoadError(null);
    advertisementService.getDetails(id)
      .then((d) => {
        if (cancelled) return;
        setExistingStatus(d.status);
        setInternalName(d.internalName || '');
        setType(d.type);
        setTitleEn(d.titleEn || '');
        setTitleAr(d.titleAr || '');
        setDescriptionEn(d.descriptionEn || '');
        setDescriptionAr(d.descriptionAr || '');
        setMediaType(d.mediaType);
        setMediaUrl(d.mediaUrl || '');
        setMediaPreviewUrl(getProductImageUrl(d.mediaUrl) || d.mediaUrl || '');
        setShowCta(d.showCta !== false);
        setCtaTextEn(d.ctaTextEn || '');
        setCtaTextAr(d.ctaTextAr || '');
        setIsClickable(d.isClickable !== false);
        setActionType(d.actionType ?? AdvertisementActionType.None);
        setActionTargetId(d.actionTargetId || null);
        setActionUrl(d.actionUrl || '');
        setStartAtLocal(toIraqLocalInputValue(d.startAtUtc));
        setExpireAtLocal(toIraqLocalInputValue(d.expireAtUtc));
        setPopupTriggerType(d.popupTriggerType ?? PopupTriggerType.Immediate);
        setPopupTriggerValue(d.popupTriggerValue != null ? String(d.popupTriggerValue) : '');
        setPopupFrequency(d.popupFrequency ?? PopupFrequency.OncePerSession);
        setNewsBarIsScrolling(!!d.newsBarIsScrolling);
        setPlacements((d.placements || []).map((p, i) => ({
          localKey: p.id || `existing-${i}`,
          id: p.id || null,
          platform: p.platform,
          page: p.page,
          slot: p.slot ?? null,
          displayOrder: p.displayOrder ?? i,
          isEnabled: p.isEnabled !== false,
        })));
        prevTypeRef.current = d.type;
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.response?.data?.message || err.message || (isRtl ? 'تعذر تحميل الإعلان' : 'Failed to load advertisement'));
      })
      .finally(() => { if (!cancelled) setLoadingInitial(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, id]);

  // ---- Fetch the placement registry for both platforms whenever the ad Type changes ----
  useEffect(() => {
    let cancelled = false;
    setRegistryLoading(true);
    Promise.all([
      advertisementService.getRegistry(AdvertisementPlatform.Web, type),
      advertisementService.getRegistry(AdvertisementPlatform.Mobile, type),
    ]).then(([webReg, mobileReg]) => {
      if (cancelled) return;
      setRegistryCache({
        [`${AdvertisementPlatform.Web}-${type}`]: webReg,
        [`${AdvertisementPlatform.Mobile}-${type}`]: mobileReg,
      });
    }).catch(() => { /* row selects will just show no options */ })
      .finally(() => { if (!cancelled) setRegistryLoading(false); });
    return () => { cancelled = true; };
  }, [type]);

  // ---- Changing Type invalidates existing placements (different valid Page/Slot combos) ----
  useEffect(() => {
    if (prevTypeRef.current === type) return;
    if (placements.length > 0) {
      setPendingTypeChange({ previousType: prevTypeRef.current, nextType: type });
      setType(prevTypeRef.current);
      return;
    }
    prevTypeRef.current = type;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const cancelTypeChange = () => {
    setPendingTypeChange(null);
  };

  const confirmTypeChange = () => {
    if (!pendingTypeChange) return;
    const nextType = pendingTypeChange.nextType;
    setPlacements([]);
    prevTypeRef.current = nextType;
    setPendingTypeChange(null);
    setType(nextType);
  };

  const registryFor = (platform) => registryCache[`${platform}-${type}`];

  const addPlacement = () => {
    const platform = AdvertisementPlatform.Web;
    const reg = registryFor(platform);
    const firstPage = reg?.pages?.[0];
    const firstSlot = firstPage?.slots?.[0];
    setPlacements((prev) => [...prev, {
      localKey: newPlacementKey(),
      id: null,
      platform,
      page: firstPage ? firstPage.page : AdvertisementPage.Home,
      slot: type === AdvertisementType.Popup ? null : (firstSlot ? firstSlot.slot : null),
      displayOrder: prev.length,
      isEnabled: true,
    }]);
  };

  const removePlacement = (localKey) => setPlacements((prev) => prev.filter((p) => p.localKey !== localKey));
  const updatePlacement = (localKey, patch) => setPlacements((prev) => prev.map((p) => (p.localKey === localKey ? { ...p, ...patch } : p)));

  const handlePlacementPlatformChange = (localKey, platform) => {
    const reg = registryFor(platform);
    const firstPage = reg?.pages?.[0];
    const firstSlot = firstPage?.slots?.[0];
    updatePlacement(localKey, {
      platform,
      page: firstPage ? firstPage.page : AdvertisementPage.Home,
      slot: type === AdvertisementType.Popup ? null : (firstSlot ? firstSlot.slot : null),
    });
  };

  const handlePlacementPageChange = (localKey, platform, page) => {
    const reg = registryFor(platform);
    const pageEntry = reg?.pages?.find((p) => p.page === page);
    const firstSlot = pageEntry?.slots?.[0];
    updatePlacement(localKey, {
      page,
      slot: type === AdvertisementType.Popup ? null : (firstSlot ? firstSlot.slot : null),
    });
  };

  // ---- Media ----
  const handleMediaTypeChange = (newMediaType) => {
    setMediaType(newMediaType);
    setMediaFile(null);
    setMediaUrl('');
    setMediaPreviewUrl('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleMediaFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMediaFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setMediaPreviewUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveMedia = () => {
    setMediaFile(null);
    setMediaUrl('');
    setMediaPreviewUrl('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleVideoUrlChange = (value) => {
    setMediaUrl(value);
    setMediaPreviewUrl(getProductImageUrl(value) || value);
  };

  // ---- Save ----
  const buildPayload = async (publishNow) => {
    let finalMediaUrl = mediaUrl;
    if (mediaType === AdvertisementMediaType.Image && mediaFile instanceof File) {
      const uploadResult = await imageService.uploadImages([mediaFile], 'advertisements');
      if (!uploadResult?.success || !uploadResult?.relativePaths?.length) {
        throw new Error(uploadResult?.message || (isRtl ? 'فشل رفع الصورة' : 'Image upload failed'));
      }
      [finalMediaUrl] = uploadResult.relativePaths;
    }
    if (!finalMediaUrl && type !== AdvertisementType.NewsBar) {
      throw new Error(isRtl ? 'الوسائط مطلوبة' : 'Media is required');
    }

    const isTargetAction = [AdvertisementActionType.Product, AdvertisementActionType.Category, AdvertisementActionType.Brand].includes(actionType);
    const isUrlAction = [AdvertisementActionType.ExternalUrl, AdvertisementActionType.InternalPage].includes(actionType);

    return {
      internalName: internalName.trim(),
      type,
      titleEn: titleEn.trim() || null,
      titleAr: titleAr.trim() || null,
      descriptionEn: descriptionEn.trim() || null,
      descriptionAr: descriptionAr.trim() || null,
      mediaType,
      mediaUrl: finalMediaUrl || null,
      showCta,
      ctaTextEn: ctaTextEn.trim() || null,
      ctaTextAr: ctaTextAr.trim() || null,
      isClickable,
      actionType,
      actionTargetId: isTargetAction ? actionTargetId : null,
      actionUrl: isUrlAction ? (actionUrl.trim() || null) : null,
      startAtUtc: fromIraqLocalInputValue(startAtLocal),
      expireAtUtc: fromIraqLocalInputValue(expireAtLocal),
      popupTriggerType,
      popupTriggerValue: type === AdvertisementType.Popup && popupTriggerType !== PopupTriggerType.Immediate && popupTriggerValue !== ''
        ? Number(popupTriggerValue) : null,
      popupFrequency,
      newsBarIsScrolling: type === AdvertisementType.NewsBar ? newsBarIsScrolling : false,
      placements: placements.map((p) => ({
        id: p.id || null,
        platform: p.platform,
        page: p.page,
        slot: type === AdvertisementType.Popup ? null : p.slot,
        displayOrder: p.displayOrder,
        isEnabled: p.isEnabled,
      })),
      publishNow,
    };
  };

  const handleSubmit = async (publishNow) => {
    setSaveError(null);
    if (!internalName.trim()) {
      setSaveError(isRtl ? 'الاسم الداخلي مطلوب' : 'Internal name is required');
      return;
    }
    if (type !== AdvertisementType.NewsBar) {
      if (mediaType === AdvertisementMediaType.Image && !mediaFile && !mediaUrl) {
        setSaveError(isRtl ? 'الوسائط مطلوبة' : 'Media is required');
        return;
      }
      if (mediaType === AdvertisementMediaType.Video && !mediaUrl) {
        setSaveError(isRtl ? 'رابط الفيديو مطلوب' : 'Video URL is required');
        return;
      }
    }
    if (publishNow && placements.length === 0) {
      setSaveError(isRtl ? 'أضف موضعاً واحداً على الأقل قبل النشر' : 'Add at least one placement before publishing');
      return;
    }
    setIsSaving(true);
    try {
      const payload = await buildPayload(publishNow);
      if (mode === 'edit') {
        await advertisementService.update(id, payload);
      } else {
        await advertisementService.create(payload);
      }
      navigate('/advertisements');
    } catch (err) {
      setSaveError(err.response?.data?.message || err.message || (isRtl ? 'حدث خطأ' : 'Something went wrong'));
    } finally {
      setIsSaving(false);
    }
  };

  if (loadingInitial) {
    return <Loading text={isRtl ? 'جاري التحميل...' : 'Loading...'} />;
  }

  if (loadError) {
    return (
      <div className="page-content">
        <div className="error-alert"><span>{loadError}</span></div>
        <Button variant="secondary" onClick={() => navigate('/advertisements')}>{isRtl ? '← العودة' : '← Back'}</Button>
      </div>
    );
  }

  const isDraftEligible = mode === 'create' || existingStatus === AdvertisementStatus.Draft;
  const isTargetAction = [AdvertisementActionType.Product, AdvertisementActionType.Category, AdvertisementActionType.Brand].includes(actionType);
  const isUrlAction = [AdvertisementActionType.ExternalUrl, AdvertisementActionType.InternalPage].includes(actionType);

  return (
    <div className="page-content promo-form-page">
      {saveError && (
        <div className="error-alert" style={{ marginBottom: 'var(--sp-5)' }}><span>{saveError}</span></div>
      )}

      <div className="promo-form-layout">
        <div className="promo-form-main">
          <div className="card">
            <div className="card-body">
              <form onSubmit={(e) => e.preventDefault()}>

                {/* General */}
                <section className="form-section">
                  <h4 className="form-section-title">{isRtl ? 'معلومات عامة' : 'General'}</h4>
                  <Input
                    label={isRtl ? 'الاسم الداخلي (للإدارة فقط)' : 'Internal Name (admin-only)'}
                    value={internalName}
                    onChange={(e) => setInternalName(e.target.value)}
                    placeholder={isRtl ? 'مثال: حملة صيف 2026' : 'e.g., Summer 2026 Campaign'}
                    required
                  />
                  <div className="form-row form-row-2">
                    <Input
                      label={isRtl ? 'العنوان (عربي)' : 'Title (Arabic)'}
                      value={titleAr}
                      onChange={(e) => setTitleAr(e.target.value)}
                    />
                    <Input
                      label={isRtl ? 'العنوان (إنجليزي)' : 'Title (English)'}
                      value={titleEn}
                      onChange={(e) => setTitleEn(e.target.value)}
                    />
                  </div>
                  <div className="form-row form-row-2">
                    <div className="form-group">
                      <label className="form-label">{isRtl ? 'الوصف (عربي)' : 'Description (Arabic)'}</label>
                      <textarea className="form-textarea" rows={3} value={descriptionAr} onChange={(e) => setDescriptionAr(e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{isRtl ? 'الوصف (إنجليزي)' : 'Description (English)'}</label>
                      <textarea className="form-textarea" rows={3} value={descriptionEn} onChange={(e) => setDescriptionEn(e.target.value)} />
                    </div>
                  </div>
                </section>

                {/* Platform & Type + Placements */}
                <section className="form-section">
                  <h4 className="form-section-title">{isRtl ? 'النوع والمواضع' : 'Type & Placements'}</h4>
                  <div className="form-group">
                    <label className="form-label">{isRtl ? 'نوع الإعلان' : 'Advertisement Type'}</label>
                    <select className="form-select" value={type} onChange={(e) => setType(Number(e.target.value))}>
                      {Object.values(AdvertisementType).map((v) => (
                        <option key={v} value={v}>{labelFor(TYPE_LABELS, v, isRtl)}</option>
                      ))}
                    </select>
                    <p className="form-hint">
                      {isRtl
                        ? 'اختر النوع أولاً — يحدد الصفحات والمواضع المتاحة لكل موضع.'
                        : 'Choose the type first — it determines which pages and slots are available for each placement.'}
                    </p>
                  </div>

                  <div className="placements-list">
                    {placements.length === 0 && (
                      <div className="placements-empty">
                        {isRtl ? 'لم تتم إضافة أي مواضع بعد.' : 'No placements added yet.'}
                      </div>
                    )}
                    {placements.map((p) => {
                      const reg = registryFor(p.platform);
                      const pages = reg?.pages || [];
                      const pageEntry = pages.find((pg) => pg.page === p.page);
                      const slots = pageEntry?.slots || [];
                      return (
                        <div key={p.localKey} className="placement-row">
                          <div className="placement-row-fields">
                            <div className="form-group">
                              <label className="form-label">{isRtl ? 'المنصة' : 'Platform'}</label>
                              <select
                                className="form-select"
                                value={p.platform}
                                onChange={(e) => handlePlacementPlatformChange(p.localKey, Number(e.target.value))}
                              >
                                <option value={AdvertisementPlatform.Web}>{labelFor(PLATFORM_LABELS, AdvertisementPlatform.Web, isRtl)}</option>
                                <option value={AdvertisementPlatform.Mobile}>{labelFor(PLATFORM_LABELS, AdvertisementPlatform.Mobile, isRtl)}</option>
                              </select>
                            </div>
                            <div className="form-group">
                              <label className="form-label">{isRtl ? 'الصفحة' : 'Page'}</label>
                              <select
                                className="form-select"
                                value={p.page}
                                disabled={!reg}
                                onChange={(e) => handlePlacementPageChange(p.localKey, p.platform, Number(e.target.value))}
                              >
                                {pages.map((pg) => (
                                  <option key={pg.page} value={pg.page}>{isRtl ? pg.nameAr : pg.nameEn}</option>
                                ))}
                              </select>
                            </div>
                            {type !== AdvertisementType.Popup && (
                              <div className="form-group">
                                <label className="form-label">{isRtl ? 'الموضع' : 'Slot'}</label>
                                <select
                                  className="form-select"
                                  value={p.slot ?? ''}
                                  disabled={!reg || slots.length === 0}
                                  onChange={(e) => updatePlacement(p.localKey, { slot: Number(e.target.value) })}
                                >
                                  {slots.map((s) => (
                                    <option key={s.slot} value={s.slot}>{isRtl ? s.nameAr : s.nameEn}</option>
                                  ))}
                                </select>
                              </div>
                            )}
                            <div className="form-group placement-order-field">
                              <label className="form-label">{isRtl ? 'الترتيب' : 'Order'}</label>
                              <input
                                type="number"
                                className="form-input"
                                min={0}
                                value={p.displayOrder}
                                onChange={(e) => updatePlacement(p.localKey, { displayOrder: Number(e.target.value) || 0 })}
                              />
                            </div>
                            <div className="form-group placement-enabled-field">
                              <label className="toggle-switch">
                                <input type="checkbox" checked={p.isEnabled} onChange={(e) => updatePlacement(p.localKey, { isEnabled: e.target.checked })} />
                                <span className="toggle-slider"></span>
                                <span className="toggle-label">{isRtl ? 'مفعل' : 'Enabled'}</span>
                              </label>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="placement-remove-btn"
                            onClick={() => removePlacement(p.localKey)}
                            aria-label={isRtl ? 'إزالة الموضع' : 'Remove placement'}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <Button type="button" variant="secondary" onClick={addPlacement} disabled={registryLoading}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" /></svg>
                    {isRtl ? 'إضافة موضع' : 'Add Placement'}
                  </Button>
                </section>

                {/* Content: Media — News Bar is text-only and never displays media */}
                {type !== AdvertisementType.NewsBar && (
                <section className="form-section">
                  <h4 className="form-section-title">{isRtl ? 'الوسائط' : 'Media'}</h4>
                  <div className="form-group">
                    <label className="form-label">{isRtl ? 'نوع الوسائط' : 'Media Type'}</label>
                    <select className="form-select" value={mediaType} onChange={(e) => handleMediaTypeChange(Number(e.target.value))}>
                      <option value={AdvertisementMediaType.Image}>{isRtl ? 'صورة' : 'Image'}</option>
                      <option value={AdvertisementMediaType.Video}>{isRtl ? 'فيديو (رابط)' : 'Video (URL)'}</option>
                    </select>
                  </div>

                  {mediaType === AdvertisementMediaType.Image ? (
                    <div
                      className="image-upload-zone"
                      onClick={() => fileInputRef.current?.click()}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleMediaFileChange}
                        className="image-upload-input"
                        aria-hidden
                      />
                      {mediaPreviewUrl ? (
                        <div className="image-upload-preview">
                          <img src={mediaPreviewUrl} alt="Preview" />
                          <button
                            type="button"
                            className="image-upload-remove"
                            onClick={(e) => { e.stopPropagation(); handleRemoveMedia(); }}
                            aria-label={isRtl ? 'إزالة الصورة' : 'Remove image'}
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="image-upload-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                              <circle cx="8.5" cy="8.5" r="1.5" />
                              <polyline points="21 15 16 10 5 21" />
                            </svg>
                          </div>
                          <span className="image-upload-text">{isRtl ? 'انقر لرفع الصورة' : 'Click to upload image'}</span>
                        </>
                      )}
                    </div>
                  ) : (
                    <Input
                      label={isRtl ? 'رابط الفيديو' : 'Video URL'}
                      type="url"
                      value={mediaUrl}
                      onChange={(e) => handleVideoUrlChange(e.target.value)}
                      placeholder="https://..."
                    />
                  )}
                </section>
                )}

                {/* CTA */}
                <section className="form-section">
                  <h4 className="form-section-title">{isRtl ? 'زر الإجراء (CTA)' : 'Call To Action'}</h4>
                  <div className="form-group">
                    <label className="toggle-switch">
                      <input type="checkbox" checked={showCta} onChange={(e) => setShowCta(e.target.checked)} />
                      <span className="toggle-slider"></span>
                      <span className="toggle-label">{isRtl ? 'إظهار الزر' : 'Show CTA button'}</span>
                    </label>
                  </div>
                  <div className="form-row form-row-2">
                    <Input
                      label={isRtl ? 'نص الزر (عربي)' : 'Button Text (Arabic)'}
                      value={ctaTextAr}
                      onChange={(e) => setCtaTextAr(e.target.value)}
                      disabled={!showCta}
                      placeholder={isRtl ? 'تسوق الآن' : 'e.g., تسوق الآن'}
                    />
                    <Input
                      label={isRtl ? 'نص الزر (إنجليزي)' : 'Button Text (English)'}
                      value={ctaTextEn}
                      onChange={(e) => setCtaTextEn(e.target.value)}
                      disabled={!showCta}
                      placeholder="e.g., Shop Now"
                    />
                  </div>
                  <div className="form-group">
                    <label className="toggle-switch">
                      <input type="checkbox" checked={isClickable} onChange={(e) => setIsClickable(e.target.checked)} />
                      <span className="toggle-slider"></span>
                      <span className="toggle-label">{isRtl ? 'الإعلان بأكمله قابل للنقر' : 'Whole ad is clickable'}</span>
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{isRtl ? 'نوع الإجراء' : 'Action Type'}</label>
                    <select className="form-select" value={actionType} onChange={(e) => { setActionType(Number(e.target.value)); setActionTargetId(null); setActionUrl(''); }}>
                      {Object.values(AdvertisementActionType).map((v) => (
                        <option key={v} value={v}>{labelFor(ACTION_TYPE_LABELS, v, isRtl)}</option>
                      ))}
                    </select>
                  </div>
                  {isTargetAction && (
                    <div className="form-group">
                      <label className="form-label">{labelFor(ACTION_TYPE_LABELS, actionType, isRtl)}</label>
                      <TargetPicker actionType={actionType} value={actionTargetId} onChange={setActionTargetId} isRtl={isRtl} />
                    </div>
                  )}
                  {isUrlAction && (
                    <Input
                      label={actionType === AdvertisementActionType.ExternalUrl
                        ? (isRtl ? 'الرابط الخارجي' : 'External URL')
                        : (isRtl ? 'مسار الصفحة الداخلية' : 'Internal Page Path')}
                      type={actionType === AdvertisementActionType.ExternalUrl ? 'url' : 'text'}
                      value={actionUrl}
                      onChange={(e) => setActionUrl(e.target.value)}
                      placeholder={actionType === AdvertisementActionType.ExternalUrl ? 'https://...' : '/promotions/summer'}
                    />
                  )}
                </section>

                {/* Display Rules — type-specific */}
                {type === AdvertisementType.Popup && (
                  <section className="form-section">
                    <h4 className="form-section-title">{isRtl ? 'قواعد العرض' : 'Display Rules'}</h4>
                    <div className="form-row form-row-2">
                      <div className="form-group">
                        <label className="form-label">{isRtl ? 'وقت الظهور' : 'Trigger'}</label>
                        <select className="form-select" value={popupTriggerType} onChange={(e) => setPopupTriggerType(Number(e.target.value))}>
                          {Object.values(PopupTriggerType).map((v) => (
                            <option key={v} value={v}>{labelFor(TRIGGER_TYPE_LABELS, v, isRtl)}</option>
                          ))}
                        </select>
                      </div>
                      {popupTriggerType !== PopupTriggerType.Immediate && (
                        <Input
                          label={popupTriggerType === PopupTriggerType.AfterSeconds
                            ? (isRtl ? 'عدد الثواني' : 'Seconds')
                            : (isRtl ? 'نسبة التمرير %' : 'Scroll %')}
                          type="number"
                          min={0}
                          value={popupTriggerValue}
                          onChange={(e) => setPopupTriggerValue(e.target.value)}
                        />
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">{isRtl ? 'التكرار' : 'Frequency'}</label>
                      <select className="form-select" value={popupFrequency} onChange={(e) => setPopupFrequency(Number(e.target.value))}>
                        {Object.values(PopupFrequency).map((v) => (
                          <option key={v} value={v}>{labelFor(FREQUENCY_LABELS, v, isRtl)}</option>
                        ))}
                      </select>
                    </div>
                  </section>
                )}

                {type === AdvertisementType.NewsBar && (
                  <section className="form-section">
                    <h4 className="form-section-title">{isRtl ? 'قواعد العرض' : 'Display Rules'}</h4>
                    <div className="form-group">
                      <label className="toggle-switch">
                        <input type="checkbox" checked={newsBarIsScrolling} onChange={(e) => setNewsBarIsScrolling(e.target.checked)} />
                        <span className="toggle-slider"></span>
                        <span className="toggle-label">{isRtl ? 'نص متحرك (Ticker)' : 'Scrolling ticker text'}</span>
                      </label>
                    </div>
                  </section>
                )}

                {/* Schedule */}
                <section className="form-section">
                  <h4 className="form-section-title">{isRtl ? 'الجدولة (بتوقيت بغداد UTC+3)' : 'Schedule (Baghdad time, UTC+3)'}</h4>
                  <div className="form-row form-row-2">
                    <div className="form-group">
                      <label className="form-label">{isRtl ? 'تاريخ البدء' : 'Start Date'}</label>
                      <input type="datetime-local" className="form-input" value={startAtLocal} onChange={(e) => setStartAtLocal(e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{isRtl ? 'تاريخ الانتهاء' : 'Expiration Date'}</label>
                      <input type="datetime-local" className="form-input" value={expireAtLocal} onChange={(e) => setExpireAtLocal(e.target.value)} />
                    </div>
                  </div>
                  <p className="form-hint">
                    {isRtl
                      ? 'اتركهما فارغتين للنشر الفوري بدون تاريخ انتهاء.'
                      : 'Leave both empty to publish immediately with no expiration.'}
                  </p>
                </section>

                <div className="promo-form-actions">
                  {isDraftEligible && (
                    <Button type="button" variant="secondary" disabled={isSaving} onClick={() => handleSubmit(false)}>
                      {isRtl ? 'حفظ كمسودة' : 'Save Draft'}
                    </Button>
                  )}
                  <Button type="button" variant="primary" disabled={isSaving} onClick={() => handleSubmit(true)}>
                    {isSaving
                      ? (isRtl ? 'جاري الحفظ...' : 'Saving...')
                      : (isDraftEligible ? (isRtl ? 'نشر' : 'Publish') : (isRtl ? 'حفظ التغييرات' : 'Save Changes'))}
                  </Button>
                  <Button type="button" variant="secondary" disabled={isSaving} onClick={() => navigate('/advertisements')}>
                    {isRtl ? 'إلغاء' : 'Cancel'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="promo-form-preview-col">
          <AdvertisementPreview
            type={type}
            placements={placements}
            mediaType={mediaType}
            mediaPreviewUrl={mediaPreviewUrl}
            titleEn={titleEn}
            titleAr={titleAr}
            descriptionEn={descriptionEn}
            descriptionAr={descriptionAr}
            showCta={showCta}
            ctaTextEn={ctaTextEn}
            ctaTextAr={ctaTextAr}
            newsBarIsScrolling={newsBarIsScrolling}
            isRtl={isRtl}
          />
        </div>
      </div>

      <Modal
        isOpen={!!pendingTypeChange}
        onClose={cancelTypeChange}
        title={isRtl ? 'تغيير نوع الإعلان' : 'Change Advertisement Type'}
        size="small"
        footer={(
          <>
            <Button variant="secondary" onClick={cancelTypeChange}>
              {isRtl ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button variant="primary" onClick={confirmTypeChange}>
              {isRtl ? 'متابعة' : 'Continue'}
            </Button>
          </>
        )}
      >
        <p style={{ margin: 0, color: 'var(--text-secondary)', lineHeight: '1.8' }}>
          {isRtl
            ? 'تغيير نوع الإعلان سيعيد تعيين كل المواضع المضافة. هل تريد المتابعة؟'
            : 'Changing the advertisement type will reset all added placements. Continue?'}
        </p>
      </Modal>

      <style>{`
        .promo-form-page {
          height: calc(100vh - var(--header-h) - (var(--sp-8) * 2) + var(--sp-7));
          margin-bottom: calc(-1 * var(--sp-7));
          min-height: 0;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .promo-form-page > .page-header,
        .promo-form-page > .error-alert {
          flex-shrink: 0;
        }
        .promo-form-layout {
          display: grid;
          grid-template-columns: 1fr 420px;
          gap: var(--sp-6);
          align-items: stretch;
          flex: 1 1 auto;
          min-height: 0;
          overflow: hidden;
        }
        .promo-form-main {
          min-height: 0;
          overflow-y: auto;
          overflow-x: hidden;
          padding-inline-end: var(--sp-2);
        }
        .promo-form-main > .card {
          min-height: 100%;
        }
        .promo-form-preview-col {
          min-height: 0;
          height: 100%;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .promo-form-preview-col > .card {
          flex: 1 1 auto;
          min-height: 0;
          height: 100%;
        }
        .promo-form-preview-col > .card .card-body {
          height: calc(100% - 54px);
          overflow: hidden;
          display: flex;
          align-items: flex-start;
          justify-content: center;
        }
        @media (max-width: 1100px) {
          .promo-form-page {
            height: auto;
            margin-bottom: 0;
            overflow: visible;
            display: block;
          }
          .promo-form-layout { grid-template-columns: 1fr; }
          .promo-form-main {
            overflow: visible;
            padding-inline-end: 0;
          }
          .promo-form-preview-col {
            height: auto;
            overflow: visible;
          }
          .promo-form-preview-col > .card,
          .promo-form-preview-col > .card .card-body {
            height: auto;
          }
        }

        .placements-list { display: flex; flex-direction: column; gap: var(--sp-3); margin-bottom: var(--sp-4); }
        .placements-empty {
          padding: var(--sp-4);
          text-align: center;
          color: var(--text-muted);
          font-size: var(--fs-sm);
          background: var(--neutral-50);
          border: 1px dashed var(--border);
          border-radius: var(--r-md);
        }
        .placement-row {
          display: flex;
          align-items: flex-end;
          gap: var(--sp-3);
          padding: var(--sp-4);
          background: var(--neutral-50);
          border: 1px solid var(--border);
          border-radius: var(--r-md);
        }
        .placement-row-fields {
          flex: 1;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: var(--sp-3);
          align-items: end;
        }
        .placement-row-fields .form-group { margin-bottom: 0; }
        .placement-order-field input { max-width: 100px; }
        .placement-enabled-field { display: flex; align-items: center; padding-bottom: 8px; }
        .placement-remove-btn {
          flex-shrink: 0;
          width: 32px; height: 32px;
          display: flex; align-items: center; justify-content: center;
          border: 1px solid var(--border);
          background: var(--surface);
          color: var(--danger);
          border-radius: var(--r-md);
          cursor: pointer;
        }
        .placement-remove-btn:hover { background: var(--danger-light); border-color: var(--danger); }

        .promo-form-actions {
          display: flex;
          gap: var(--sp-3);
          margin-top: var(--sp-6);
          padding-top: var(--sp-4);
          border-top: 1px solid var(--border);
        }

        @media (max-width: 640px) {
          .placement-row { flex-direction: column; align-items: stretch; }
          .placement-remove-btn { align-self: flex-end; }
        }
      `}</style>
    </div>
  );
};

export default AdvertisementForm;
